import { WorkspacesAPITagType } from '@/api/api'
import { userApi } from '@/api/user-api'
import getWorkspaceApiUrl from '@/api/utils/get-workspace-api-url'
import { workspacesApi } from '@/api/workspaces-api'
import {
  DEMO_REPORT_ID,
  makeDemoInsightReportCommentAnalysisResponse,
  makeDemoInsightReportCommentsResponse,
  makeDemoInsightReportCustomPostTypesResponse,
  makeDemoInsightReportInfinitePostsResponse,
  makeDemoInsightReportKolAnalysisResponse,
  makeDemoInsightReportKolsResponse,
  makeDemoInsightReportOverviewResponse,
  makeDemoInsightReportPerformanceResponse,
} from '@/constants/demo-report'
import { RootState } from '@/store/store'
import {
  AddKolsToInsightReportRequest,
  CreateInsightReportPostRequest,
  CreateInsightReportRequest,
  CreateInsightReportResponse,
  createInsightReportResponseSchema,
  DeleteInsightReportHandFilledPostRequest,
  DeleteInsightReportRequest,
  ExportInsightReportPptxRequest,
  ExportPptxResponse,
  exportPptxResponseSchema,
  FetchInsightReportKolsRequest,
  InsightReportCommentAnalysisRequest,
  InsightReportCommentAnalysisResponse,
  insightReportCommentAnalysisSchema,
  InsightReportCommentsRequest,
  InsightReportCommentsResponse,
  insightReportCommentsSchema,
  InsightReportCustomPostTypesRequest,
  InsightReportCustomPostTypesResponse,
  insightReportCustomPostTypesSchema,
  InsightReportInfinitePostsResponse,
  insightReportInfinitePostsSchema,
  InsightReportKolAnalysisRequest,
  InsightReportKolAnalysisResponse,
  insightReportKolAnalysisSchema,
  insightReportKolPerformanceDataSchema,
  InsightReportKolPerformanceRequest,
  InsightReportKolPerformanceResponse,
  InsightReportKolsResponse,
  insightReportKolsSchema,
  InsightReportListRequest,
  InsightReportListResponse,
  insightReportListResponseSchema,
  InsightReportOverviewRequest,
  InsightReportOverviewResponse,
  insightReportOverviewSchema,
  InsightReportOwnersResponse,
  insightReportOwnersResponseSchema,
  InsightReportPostsRequest,
  RemoveKolsFromInsightReportRequest,
  TriggerCrawlersRequest,
  TriggerOverviewAiSummaryRequest,
  UpdateCommentTermsRequest,
  UpdateInsightReportPostRequest,
  UpdateInsightReportRequest,
} from '@/types/schema/api/insight-report'
import { filterResourceType } from '@/types/schema/directory/kol-management'
import { toSupportedLanguage } from '@/utils/i18n/to-supported-language'
import removeNil from '@/utils/remove-nil'

const insightReportApi = workspacesApi.injectEndpoints({
  endpoints: (build) => ({
    createInsightReport: build.mutation<
      CreateInsightReportResponse,
      CreateInsightReportRequest
    >({
      query: (payload) => {
        return {
          url: getWorkspaceApiUrl('/insight-reports', 'v4'),
          method: 'POST',
          body: payload,
        }
      },
      extraOptions: {
        dataSchema: createInsightReportResponseSchema,
      },
      invalidatesTags: [
        WorkspacesAPITagType.InsightReportList,
        WorkspacesAPITagType.InsightReportOwners,
      ],
    }),
    updateInsightReport: build.mutation<void, UpdateInsightReportRequest>({
      query: ({ report_id, ...payload }) => {
        return {
          url: getWorkspaceApiUrl(`/insight-reports/${report_id}`, 'v4'),
          method: 'PUT',
          body: payload,
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.InsightReport,
        WorkspacesAPITagType.InsightReportList,
        WorkspacesAPITagType.InsightReportOwners,
      ],
    }),
    deleteInsightReport: build.mutation<void, DeleteInsightReportRequest>({
      query: ({ report_id }) => {
        return {
          url: getWorkspaceApiUrl(`/insight-reports/${report_id}`, 'v4'),
          method: 'DELETE',
        }
      },
      invalidatesTags: [
        {
          type: WorkspacesAPITagType.FilterResources,
          id: filterResourceType.enum.deep_report,
        },
        WorkspacesAPITagType.InsightReportList,
        WorkspacesAPITagType.InsightReportOwners,
      ],
    }),
    fetchInsightReportOverview: build.query<
      InsightReportOverviewResponse,
      InsightReportOverviewRequest
    >({
      queryFn: async ({ report_id }, queryApi, _extraOptions, baseQuery) => {
        const state = queryApi.getState() as RootState
        const { data: userInfo } =
          userApi.endpoints.fetchUserInfo.select()(state)
        const language = toSupportedLanguage(userInfo?.language)

        if (report_id === DEMO_REPORT_ID) {
          const response = makeDemoInsightReportOverviewResponse(language)
          return { data: response }
        }

        return (await baseQuery({
          url: getWorkspaceApiUrl(
            `/insight-reports/${report_id}/overview`,
            'v4',
          ),
        })) as {
          data: InsightReportOverviewResponse
        }
      },
      extraOptions: {
        dataSchema: insightReportOverviewSchema,
      },
      providesTags: [
        WorkspacesAPITagType.InsightReport,
        WorkspacesAPITagType.InsightReportOverview,
        WorkspacesAPITagType.Demo,
      ],
    }),
    fetchInsightReportPosts: build.infiniteQuery<
      InsightReportInfinitePostsResponse,
      Omit<InsightReportPostsRequest, 'page'>,
      InsightReportPostsRequest['page']
    >({
      queryFn: async (
        { queryArg, pageParam },
        _queryApi,
        _extraOptions,
        baseQuery,
      ) => {
        const { report_id, ...args } = queryArg
        const state = _queryApi.getState() as RootState
        const { data: userInfo } =
          userApi.endpoints.fetchUserInfo.select()(state)
        const language = toSupportedLanguage(userInfo?.language)
        if (report_id === DEMO_REPORT_ID) {
          const response = makeDemoInsightReportInfinitePostsResponse(language)
          return { data: response }
        }

        const params = { ...args, page: pageParam, per_page: 10 }
        return (await baseQuery({
          url: getWorkspaceApiUrl(`/insight-reports/${report_id}/posts`, 'v4'),
          method: 'POST',
          body: removeNil(params),
        })) as {
          data: InsightReportInfinitePostsResponse
        }
      },
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          if (lastPage.total <= lastPageParam * lastPage.per_page) {
            return null
          }

          return lastPageParam + 1
        },
      },
      extraOptions: {
        dataSchema: insightReportInfinitePostsSchema,
      },
      providesTags: [
        WorkspacesAPITagType.InsightReport,
        WorkspacesAPITagType.InsightReportPosts,
        WorkspacesAPITagType.Demo,
      ],
    }),
    fetchInsightReportPostsQuery: build.query<
      InsightReportInfinitePostsResponse,
      InsightReportPostsRequest
    >({
      query: ({ report_id, ...params }) => {
        return {
          url: getWorkspaceApiUrl(`/insight-reports/${report_id}/posts`, 'v4'),
          method: 'POST',
          body: removeNil(params),
        }
      },
      extraOptions: {
        dataSchema: insightReportInfinitePostsSchema,
      },
    }),
    updateInsightReportPost: build.mutation<
      void,
      UpdateInsightReportPostRequest
    >({
      query: ({ report_id, id, ...payload }) => {
        return {
          url: getWorkspaceApiUrl(
            `/insight-reports/${report_id}/posts/${id}`,
            'v4',
          ),
          method: 'PUT',
          body: payload,
        }
      },
      invalidatesTags: [WorkspacesAPITagType.InsightReport],
    }),
    fetchInsightReportList: build.query<
      InsightReportListResponse,
      InsightReportListRequest
    >({
      query: (payload) => {
        const per_page = payload.per_page ?? 10
        const params = removeNil({ ...payload, per_page })

        return {
          url: getWorkspaceApiUrl('/insight-reports', 'v4'),
          params,
        }
      },
      extraOptions: {
        dataSchema: insightReportListResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.InsightReportList],
    }),
    fetchInsightReportCustomPostTypes: build.query<
      InsightReportCustomPostTypesResponse,
      InsightReportCustomPostTypesRequest
    >({
      queryFn: async ({ report_id }, _queryApi, _extraOptions, baseQuery) => {
        if (report_id === DEMO_REPORT_ID) {
          const response = makeDemoInsightReportCustomPostTypesResponse()
          return { data: response }
        }

        return (await baseQuery({
          url: getWorkspaceApiUrl(
            `/insight-reports/${report_id}/custom-post-types`,
            'v4',
          ),
        })) as {
          data: InsightReportCustomPostTypesResponse
        }
      },
      extraOptions: {
        dataSchema: insightReportCustomPostTypesSchema,
      },
      providesTags: [
        WorkspacesAPITagType.InsightReport,
        WorkspacesAPITagType.Demo,
      ],
    }),
    fetchInsightReportOwners: build.query<InsightReportOwnersResponse, void>({
      query: () => {
        return {
          url: getWorkspaceApiUrl('/insight-report-owners', 'v4'),
        }
      },
      extraOptions: {
        dataSchema: insightReportOwnersResponseSchema,
      },
      providesTags: [WorkspacesAPITagType.InsightReportOwners],
    }),
    fetchInsightReportComments: build.query<
      InsightReportCommentsResponse,
      InsightReportCommentsRequest
    >({
      queryFn: async (
        { report_id, ...params },
        queryApi,
        _extraOptions,
        baseQuery,
      ) => {
        const state = queryApi.getState() as RootState
        const { data: userInfo } =
          userApi.endpoints.fetchUserInfo.select()(state)
        const language = toSupportedLanguage(userInfo?.language)

        if (report_id === DEMO_REPORT_ID) {
          const response = makeDemoInsightReportCommentsResponse(language)
          return { data: response }
        }

        const body = removeNil({ ...params })
        return (await baseQuery({
          url: getWorkspaceApiUrl(
            `/insight-reports/${report_id}/comments`,
            'v4',
          ),
          body,
          method: 'POST',
        })) as {
          data: InsightReportCommentsResponse
        }
      },
      extraOptions: {
        dataSchema: insightReportCommentsSchema,
      },
      providesTags: [
        WorkspacesAPITagType.InsightReport,
        WorkspacesAPITagType.Demo,
      ],
    }),
    fetchInsightReportKolPerformance: build.query<
      InsightReportKolPerformanceResponse,
      InsightReportKolPerformanceRequest
    >({
      queryFn: async (
        { report_id, ...params },
        _queryApi,
        _extraOptions,
        baseQuery,
      ) => {
        if (report_id === DEMO_REPORT_ID) {
          const response = makeDemoInsightReportPerformanceResponse()
          return { data: response }
        }

        return (await baseQuery({
          url: getWorkspaceApiUrl(
            `/insight-reports/${report_id}/kol-performance`,
            'v4',
          ),
          body: removeNil(params),
          method: 'POST',
        })) as {
          data: InsightReportKolPerformanceResponse
        }
      },
      extraOptions: {
        dataSchema: insightReportKolPerformanceDataSchema,
      },
      providesTags: [
        WorkspacesAPITagType.InsightReport,
        WorkspacesAPITagType.Demo,
      ],
    }),
    fetchInsightReportKolAnalysis: build.query<
      InsightReportKolAnalysisResponse,
      InsightReportKolAnalysisRequest
    >({
      queryFn: async (
        { report_id, ...params },
        queryApi,
        _extraOptions,
        baseQuery,
      ) => {
        const state = queryApi.getState() as RootState
        const { data: userInfo } =
          userApi.endpoints.fetchUserInfo.select()(state)
        const language = toSupportedLanguage(userInfo?.language)

        if (report_id === DEMO_REPORT_ID) {
          const response = makeDemoInsightReportKolAnalysisResponse(language)
          return { data: response }
        }

        return (await baseQuery({
          url: getWorkspaceApiUrl(
            `/insight-reports/${report_id}/kol-analysis`,
            'v4',
          ),
          body: removeNil(params),
          method: 'POST',
        })) as {
          data: InsightReportKolAnalysisResponse
        }
      },
      extraOptions: {
        dataSchema: insightReportKolAnalysisSchema,
      },
      providesTags: [
        WorkspacesAPITagType.InsightReport,
        WorkspacesAPITagType.Demo,
      ],
    }),
    triggerCrawlers: build.mutation<void, TriggerCrawlersRequest>({
      query: ({ report_id }) => {
        return {
          url: getWorkspaceApiUrl(
            `/insight-reports/${report_id}/crawlers`,
            'v4',
          ),
          method: 'POST',
        }
      },
      invalidatesTags: [WorkspacesAPITagType.InsightReportOverview],
    }),
    fetchInsightReportKols: build.query<
      InsightReportKolsResponse,
      FetchInsightReportKolsRequest
    >({
      queryFn: async (
        { report_id, ...params },
        _queryApi,
        _extraOptions,
        baseQuery,
      ) => {
        const state = _queryApi.getState() as RootState
        const { data: userInfo } =
          userApi.endpoints.fetchUserInfo.select()(state)
        const language = toSupportedLanguage(userInfo?.language)

        if (report_id === DEMO_REPORT_ID) {
          const response = makeDemoInsightReportKolsResponse(language)
          return { data: response }
        }

        return (await baseQuery({
          url: getWorkspaceApiUrl(`/insight-reports/${report_id}/kols`, 'v4'),
          params: removeNil(params),
        })) as {
          data: InsightReportKolsResponse
        }
      },
      extraOptions: {
        dataSchema: insightReportKolsSchema,
      },
      providesTags: [
        WorkspacesAPITagType.InsightReport,
        WorkspacesAPITagType.Demo,
      ],
    }),
    addKolsToInsightReport: build.mutation<void, AddKolsToInsightReportRequest>(
      {
        query: ({ report_id, ...body }) => {
          return {
            url: getWorkspaceApiUrl(`/insight-reports/${report_id}/kols`, 'v4'),
            method: 'POST',
            body,
          }
        },
        invalidatesTags: [
          WorkspacesAPITagType.KolManagement,
          WorkspacesAPITagType.KolCollectionKols,
          WorkspacesAPITagType.InsightReport,
          WorkspacesAPITagType.InsightReportList,
        ],
      },
    ),
    removeKolsFromInsightReport: build.mutation<
      void,
      RemoveKolsFromInsightReportRequest
    >({
      query: ({ report_id, kol_uuid }) => {
        return {
          url: getWorkspaceApiUrl(
            `/insight-reports/${report_id}/kols/${kol_uuid}`,
            'v4',
          ),
          method: 'DELETE',
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.InsightReport,
        WorkspacesAPITagType.InsightReportList,
      ],
    }),
    updateCommentTerms: build.mutation<void, UpdateCommentTermsRequest>({
      query: ({ report_id, term, is_included }) => {
        return {
          url: getWorkspaceApiUrl(
            `/insight-reports/${report_id}/comment-terms/${term}`,
            'v4',
          ),
          method: 'PATCH',
          body: { is_included },
        }
      },
      invalidatesTags: [WorkspacesAPITagType.InsightReportCommentAnalysis],
    }),
    fetchInsightReportCommentAnalysis: build.query<
      InsightReportCommentAnalysisResponse,
      InsightReportCommentAnalysisRequest
    >({
      queryFn: async (
        { report_id, ...params },
        queryApi,
        _extraOptions,
        baseQuery,
      ) => {
        const state = queryApi.getState() as RootState
        const { data: userInfo } =
          userApi.endpoints.fetchUserInfo.select()(state)
        const language = toSupportedLanguage(userInfo?.language)

        if (report_id === DEMO_REPORT_ID) {
          const response =
            makeDemoInsightReportCommentAnalysisResponse(language)
          return { data: response }
        }

        return (await baseQuery({
          url: getWorkspaceApiUrl(
            `/insight-reports/${report_id}/comment-analysis`,
            'v4',
          ),
          body: removeNil(params),
          method: 'POST',
        })) as {
          data: InsightReportCommentAnalysisResponse
        }
      },
      extraOptions: {
        dataSchema: insightReportCommentAnalysisSchema,
      },
      providesTags: [
        WorkspacesAPITagType.InsightReportCommentAnalysis,
        WorkspacesAPITagType.Demo,
      ],
    }),
    createInsightReportHandFilledPost: build.mutation<
      void,
      CreateInsightReportPostRequest
    >({
      query: ({ report_id, ...payload }) => {
        const body = removeNil(payload)
        return {
          url: getWorkspaceApiUrl(
            `/insight-reports/${report_id}/handfilled-posts`,
            'v4',
          ),
          method: 'POST',
          body,
        }
      },
      invalidatesTags: [WorkspacesAPITagType.InsightReport],
    }),
    deleteInsightReportHandFilledPost: build.mutation<
      void,
      DeleteInsightReportHandFilledPostRequest
    >({
      query: ({ report_id, post_id }) => {
        return {
          url: getWorkspaceApiUrl(
            `/insight-reports/${report_id}/handfilled-posts/${post_id}`,
            'v4',
          ),
          method: 'DELETE',
        }
      },
      invalidatesTags: [
        WorkspacesAPITagType.InsightReport,
        {
          type: WorkspacesAPITagType.FilterResources,
          id: filterResourceType.enum.deep_report,
        },
      ],
    }),
    triggerOverviewAiSummary: build.mutation<
      void,
      TriggerOverviewAiSummaryRequest
    >({
      query: ({ report_id }) => {
        return {
          url: getWorkspaceApiUrl(
            `/insight-reports/${report_id}/overview-ai-summary`,
            'v4',
          ),
          method: 'POST',
        }
      },
      invalidatesTags: [WorkspacesAPITagType.InsightReportOverview],
    }),
    exportInsightReportPptx: build.mutation<
      ExportPptxResponse,
      ExportInsightReportPptxRequest
    >({
      query: ({ report_id, ...payload }) => {
        return {
          url: getWorkspaceApiUrl(
            `/insight-reports/${report_id}/export-pptx`,
            'v4',
          ),
          method: 'POST',
          body: payload,
        }
      },
      extraOptions: {
        dataSchema: exportPptxResponseSchema,
      },
    }),
  }),
  overrideExisting: false,
})

export const useCreateInsightReportMutation =
  insightReportApi.endpoints.createInsightReport.useMutation
export const useUpdateInsightReportMutation =
  insightReportApi.endpoints.updateInsightReport.useMutation
export const useDeleteInsightReportMutation =
  insightReportApi.endpoints.deleteInsightReport.useMutation
export const useFetchInsightReportListQuery =
  insightReportApi.endpoints.fetchInsightReportList.useQuery
export const useFetchInsightReportOverviewQuery =
  insightReportApi.endpoints.fetchInsightReportOverview.useQuery
export const useFetchInsightReportCommentAnalysisQuery =
  insightReportApi.endpoints.fetchInsightReportCommentAnalysis.useQuery
export const useFetchInsightReportPostsInfiniteQuery =
  insightReportApi.endpoints.fetchInsightReportPosts.useInfiniteQuery
export const useLazyFetchInsightReportPostsQuery =
  insightReportApi.endpoints.fetchInsightReportPostsQuery.useLazyQuery
export const useLazyFetchInsightReportCommentAnalysisQuery =
  insightReportApi.endpoints.fetchInsightReportCommentAnalysis.useLazyQuery
export const useFetchInsightReportOwnersQuery =
  insightReportApi.endpoints.fetchInsightReportOwners.useQuery
export const useFetchInsightReportKolPerformanceQuery =
  insightReportApi.endpoints.fetchInsightReportKolPerformance.useQuery
export const useLazyFetchInsightReportKolPerformanceQuery =
  insightReportApi.endpoints.fetchInsightReportKolPerformance.useLazyQuery
export const useTriggerCrawlersMutation =
  insightReportApi.endpoints.triggerCrawlers.useMutation
export const useUpdateCommentTermsMutation =
  insightReportApi.endpoints.updateCommentTerms.useMutation
export const useFetchInsightReportCustomPostTypesQuery =
  insightReportApi.endpoints.fetchInsightReportCustomPostTypes.useQuery
export const useFetchCommentsQuery =
  insightReportApi.endpoints.fetchInsightReportComments.useQuery
export const useLazyFetchCommentsQuery =
  insightReportApi.endpoints.fetchInsightReportComments.useLazyQuery
export const useAddKolsToInsightReportMutation =
  insightReportApi.endpoints.addKolsToInsightReport.useMutation
export const useRemoveKolsFromInsightReportMutation =
  insightReportApi.endpoints.removeKolsFromInsightReport.useMutation
export const useDeleteInsightReportHandFilledPostMutation =
  insightReportApi.endpoints.deleteInsightReportHandFilledPost.useMutation
export const useFetchInsightReportKolAnalysisQuery =
  insightReportApi.endpoints.fetchInsightReportKolAnalysis.useQuery
export const useLazyFetchInsightReportKolAnalysisQuery =
  insightReportApi.endpoints.fetchInsightReportKolAnalysis.useLazyQuery
export const useFetchInsightReportKolsQuery =
  insightReportApi.endpoints.fetchInsightReportKols.useQuery
export const useCreateInsightReportHandFilledPostMutation =
  insightReportApi.endpoints.createInsightReportHandFilledPost.useMutation
export const useUpdateInsightReportPostMutation =
  insightReportApi.endpoints.updateInsightReportPost.useMutation
export const useTriggerOverviewAiSummaryMutation =
  insightReportApi.endpoints.triggerOverviewAiSummary.useMutation
export const useExportInsightReportPptxMutation =
  insightReportApi.endpoints.exportInsightReportPptx.useMutation

export default insightReportApi
